import { createMarketTrends } from 'app/shared/models/MarketTrends';
import { growthClientWithRequestContext } from 'app/shared/utils/api';
import {
  RentalGrowthMarketTrendsSummaryWebApiHandler_summary,
  RentalGrowthTopRentalMarketsWebApiHandler_topMarkets,
} from '@zg-rentals/ts-rental-growth-api-client';
import { DatarouterError } from '@zg-rentals/web-base';
import constants from 'app/shared/flux/constants';
import pathsManifest from 'app/shared/utils/pathsManifest';
import { isEmpty } from 'app/shared/utils/marketTrendsUtils';
import MedianRentByBedroom from 'app/shared/models/MedianRentByBedroom';

const NON_CRITICAL_ERRORS = ['INVALID_INPUT'];

export const getMarketTrends = (params: {
  resourceId: string;
  bedrooms?: string;
  propertyTypes?: string;
  otherResourceId?: string;
}) => {
  const { resourceId, bedrooms, propertyTypes, otherResourceId } = params;
  // @ts-ignore
  return async (dispatch, getState) => {
    let res = {};
    let response;
    try {
      response = await RentalGrowthMarketTrendsSummaryWebApiHandler_summary(
        {
          resourceId,
          beds: bedrooms,
          propertyTypes,
          rentCompareResourceId: otherResourceId,
        },
        growthClientWithRequestContext(getState()),
      );
    } catch (error) {
      // Non-critical errors (e.g. INVALID_INPUT) leave `response` undefined, which yields empty
      // market trends and triggers the top-markets / 404 fallback below. Re-throw everything else
      // so the SSR/Filter caller can log it and return a 404.
      if (!(DatarouterError.is(error) && NON_CRITICAL_ERRORS.includes(error.code))) {
        throw error;
      }
    }

    const marketTrends = createMarketTrends(response, resourceId, otherResourceId);
    const hasFilters = bedrooms || propertyTypes;
    if (!hasFilters) {
      dispatch({ type: constants.SET_UNFILTERED_MARKET_STATS, payload: marketTrends });
    }
    if (isEmpty(marketTrends)) {
      await dispatch(fetchTopMarkets(params));
      res = { changeHttpStatus: 404 };
    }

    dispatch({ type: constants.SET_MARKET_STATS, payload: marketTrends });
    return res;
  };
};

export const getUnfilteredMarketTrends = (resourceId: string) => {
  // @ts-ignore
  return async (dispatch, getState) => {
    let response;
    try {
      response = await RentalGrowthMarketTrendsSummaryWebApiHandler_summary(
        { resourceId },
        growthClientWithRequestContext(getState()),
      );
    } catch (error) {
      if (!(DatarouterError.is(error) && NON_CRITICAL_ERRORS.includes(error.code))) {
        throw error;
      }
    }

    dispatch({
      type: constants.SET_UNFILTERED_MARKET_STATS,
      payload: createMarketTrends(response, resourceId),
    });
    return {};
  };
};

export const fetchTopMarkets = (params: { bedrooms?: string; propertyTypes?: string }) => {
  const { bedrooms, propertyTypes } = params;
  // @ts-ignore
  return async (dispatch, getState) => {
    const { topRentalMarkets } = await RentalGrowthTopRentalMarketsWebApiHandler_topMarkets(
      { beds: bedrooms, propertyTypes },
      growthClientWithRequestContext(getState()),
    );
    dispatch({
      type: constants.SET_TOP_MARKETS,
      payload: {
        topRentalMarkets: topRentalMarkets.map((market) => ({
          url: `${pathsManifest.MarketTrendsPage}/${market.resourceId}/`,
          resourceId: market.resourceId,
          name: market.areaName,
          medianRent: market.medianRent,
          prices: market.medianRentPriceOverTime,
        })),
      },
    });
    return {};
  };
};

export const getMedianRentByBedroom = (resourceId: string) => {
  // @ts-ignore
  return async (dispatch, getState) => {
    const client = growthClientWithRequestContext(getState());

    // The /summary endpoint returns one market summary per request, so fetch each bed count in
    // parallel. Swallow non-critical errors (e.g. INVALID_INPUT) as a null median; re-throw the
    // rest for the SSR caller to log.
    const fetchMedianRent = async (beds: string): Promise<number | null> => {
      try {
        const response = await RentalGrowthMarketTrendsSummaryWebApiHandler_summary({ resourceId, beds }, client);
        return response?.summary?.medianRent ?? null;
      } catch (error) {
        if (!(DatarouterError.is(error) && NON_CRITICAL_ERRORS.includes(error.code))) {
          throw error;
        }
        return null;
      }
    };

    const [
      studioMedianRent,
      oneBedroomMedianRent,
      twoBedroomMedianRent,
      threeBedroomMedianRent,
      fourPlusBedroomMedianRent,
    ] = await Promise.all([
      fetchMedianRent('0'),
      fetchMedianRent('1'),
      fetchMedianRent('2'),
      fetchMedianRent('3'),
      fetchMedianRent('4plus'),
    ]);

    dispatch({
      type: constants.SET_MEDIAN_RENT_BY_BEDROOM,
      payload: new MedianRentByBedroom({
        studioMedianRent,
        oneBedroomMedianRent,
        twoBedroomMedianRent,
        threeBedroomMedianRent,
        fourPlusBedroomMedianRent,
      }),
    });
    return {};
  };
};
